import {Input} from '@/base/ui/input'
import {Label} from '@/base/ui/label'
import {cn, getLabelPositionClass, randomId} from '@/lib/utils'
import React, {forwardRef, useState} from 'react'
import {WuButton} from '../button'

export interface IWuInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  labelPosition?: 'left' | 'top' | 'right'
  Icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  variant?: 'flat' | 'outlined'
  readonly?: boolean
}

export const WuInput = forwardRef<HTMLInputElement, IWuInputProps>(
  (props, ref) => {
    const {
      label,
      labelPosition = 'top',
      Icon,
      iconPosition,
      type,
      value,
      variant = 'flat',
      readonly,
      id = randomId(),
      ...rest
    } = props

    const customLabelPosition = getLabelPositionClass(labelPosition)

    const inputWithIconClass = Icon
      ? iconPosition !== 'right'
        ? 'pl-8'
        : 'pr-8'
      : ''
    const [showPassword, setShowPassword] = useState(false)
    const handleClickShowPassword = (): void => {
      setShowPassword(prevShowPassword => !prevShowPassword)
    }

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (rest.onChange) rest.onChange(e)
    }

    return (
      <>
        {type === 'submit' ? (
          <WuButton type="submit" className={rest.className} id={id}>
            {label || 'Submit'}
          </WuButton>
        ) : (
          <div
            className={cn('flex flex-col gap-2 wu-input-container')}
            dir={rest.dir}
          >
            <div
              className={cn(
                'flex gap-1 relative',
                customLabelPosition,
                (labelPosition === 'right' || labelPosition === 'left') &&
                  'items-center',
                'wu-input-wrapper',
              )}
            >
              {Icon && iconPosition !== 'right' && (
                <div className="absolute flex items-center justify-center w-8 h-8 wu-input-icon-container">
                  {Icon}
                </div>
              )}

              <Input
                {...rest}
                className={cn(
                  'peer text-xs rounded-t-sm',
                  variant === 'outlined' ? 'border border-gray-subtle' : '',
                  inputWithIconClass,
                  rest.className,
                )}
                type={showPassword ? 'text' : type}
                ref={ref}
                readOnly={readonly}
                onChange={rest.onChange && handleOnChange}
                value={value}
                id={id}
              />

              {Icon && iconPosition === 'right' && (
                <div className="absolute right-0 flex items-center justify-center w-8 h-8 wu-input-icon-container">
                  {Icon}
                </div>
              )}
              {type === 'password' && (
                <div
                  className="absolute right-0 flex items-center justify-center w-8 h-8 cursor-pointer"
                  onClick={handleClickShowPassword}
                >
                  <button
                    className="flex items-center justify-center w-6 h-6 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-soft"
                    aria-label="show-password"
                  >
                    {showPassword ? (
                      <span className="wm-visibility text-gray-subtle"></span>
                    ) : (
                      <span className="wm-visibility-off text-gray-subtle"></span>
                    )}
                  </button>
                </div>
              )}
              {label && (
                <Label
                  htmlFor={id}
                  className="flex-auto text-sm font-normal leading-6 text-gray-lead whitespace-nowrap  truncate max-w-[120px] wu-input-label"
                >
                  {label}
                </Label>
              )}
            </div>
          </div>
        )}
      </>
    )
  },
)

WuInput.displayName = 'WuInput'
